import React, { useState } from "react";
import { Alert, Button, Form, Space, Typography, message } from "antd";
import { PlusOutlined, SaveOutlined } from "@ant-design/icons";

import { createTransactionReceipt } from "../../api/transactionReceiptApi";
import { useTranslation } from "../../i18n/LanguageProvider";
import TransactionReceiptFormFields from "./TransactionReceiptFormFields";
import { buildTransactionReceiptPayload } from "./transactionReceiptUi";

const { Title, Text } = Typography;

/**
 * Manual entry of one Гүйлгээний Баримт row. The fields mirror the Excel structure so both
 * import paths write the same table. The PDF is attached afterwards, from the row's own upload
 * button in the table.
 */
function ManualTransactionReceiptForm({ onSaved }) {
    const { t } = useTranslation();
    const [form] = Form.useForm();
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");

    const handleSubmit = async () => {
        setError("");

        try {
            const values = await form.validateFields();

            setSaving(true);

            const saved = await createTransactionReceipt(buildTransactionReceiptPayload(values));

            message.success(t("receipt.manual.saved"));
            form.resetFields();
            onSaved?.(saved);
        } catch (err) {
            if (err?.errorFields) return;

            const text = err?.message || t("receipt.manual.saveFailed");
            setError(text);
            message.error(text);
        } finally {
            setSaving(false);
        }
    };

    return (
        <Space direction="vertical" size={16} style={{ width: "100%" }}>
            <div>
                <Title level={4} className="!mb-1">
                    <Space>
                        <PlusOutlined />
                        {t("receipt.manual.title")}
                    </Space>
                </Title>
                <Text type="secondary">{t("receipt.manual.hint")}</Text>
            </div>

            {error && (
                <Alert type="error" showIcon message={t("manual.saveError")} description={error} />
            )}

            <Form form={form} layout="vertical" requiredMark autoComplete="off">
                <TransactionReceiptFormFields t={t} />

                <Space>
                    <Button
                        type="primary"
                        icon={<SaveOutlined />}
                        loading={saving}
                        onClick={handleSubmit}
                    >
                        {t("action.save")}
                    </Button>
                    <Button
                        onClick={() => {
                            form.resetFields();
                            setError("");
                        }}
                    >
                        {t("action.clear")}
                    </Button>
                </Space>
            </Form>
        </Space>
    );
}

export default ManualTransactionReceiptForm;
